/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { fetchOntologyApi, resolveOntologyTenantId } from './ontology-request';
import { resolveOntologyRequestUrl } from './ontology-api-env';
import {
  ONTOLOGY_APPS_API_URL,
  ONTOLOGY_APPS_PAGE_SIZE,
  ONTOLOGY_APPS_STATUS_PUBLISHED,
} from './constants';

export interface OntologyAppItem {
  app_id: string;
  name: string;
  description?: string;
  status?: string;
  namespace?: string;
  sr_mapping_published?: boolean;
}

interface OntologyAppsApiItem {
  id: number;
  name: string;
  description?: string;
  status?: string;
  namespace?: string;
  tenant_id?: string;
  sr_mapping_published?: boolean;
}

interface OntologyAppsApiResponse {
  ok?: boolean;
  data?: {
    items?: OntologyAppsApiItem[];
    page?: number;
    page_size?: number;
    total?: number;
  };
}

function mapAppItem(item: OntologyAppsApiItem): OntologyAppItem {
  return {
    app_id: String(item.id),
    name: item.name,
    description: item.description,
    status: item.status,
    namespace: item.namespace,
    sr_mapping_published: item.sr_mapping_published,
  };
}

/** 切模式校验和下拉共用同一次结果；过期后再拉，失败不留下。 */
const PUBLISHED_APPS_CACHE_MS = 60_000;
const publishedAppsByTenant = new Map<
  string,
  { expiresAt: number; pending: Promise<OntologyAppItem[]> }
>();

async function loadPublishedApps(tenantId: string): Promise<OntologyAppItem[]> {
  const url = resolveOntologyRequestUrl(ONTOLOGY_APPS_API_URL);
  url.searchParams.set('tenant_id', tenantId);
  url.searchParams.set('page', '1');
  url.searchParams.set('page_size', String(ONTOLOGY_APPS_PAGE_SIZE));
  url.searchParams.set('status', ONTOLOGY_APPS_STATUS_PUBLISHED);
  url.searchParams.set('lite', '1');

  const response = await fetchOntologyApi(url.toString());
  if (!response.ok) {
    throw new Error(`fetchOntologyApps failed: ${response.status}`);
  }

  const json = (await response.json()) as OntologyAppsApiResponse;
  return (json.data?.items ?? []).map(mapAppItem);
}

export async function fetchOntologyApps(): Promise<OntologyAppItem[]> {
  const tenantId = await resolveOntologyTenantId();
  const cached = publishedAppsByTenant.get(tenantId);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.pending;
  }

  const pending = loadPublishedApps(tenantId).catch(error => {
    const current = publishedAppsByTenant.get(tenantId);
    if (current?.pending === pending) {
      publishedAppsByTenant.delete(tenantId);
    }
    throw error;
  });
  publishedAppsByTenant.set(tenantId, {
    expiresAt: Date.now() + PUBLISHED_APPS_CACHE_MS,
    pending,
  });
  return pending;
}

export async function fetchOntologyAppsTotal(): Promise<number> {
  const url = resolveOntologyRequestUrl(ONTOLOGY_APPS_API_URL);
  url.searchParams.set('tenant_id', await resolveOntologyTenantId());
  url.searchParams.set('page', '1');
  url.searchParams.set('page_size', '1');
  url.searchParams.set('status', ONTOLOGY_APPS_STATUS_PUBLISHED);
  url.searchParams.set('lite', '1');

  const response = await fetchOntologyApi(url.toString());
  if (!response.ok) {
    throw new Error(`fetchOntologyAppsTotal failed: ${response.status}`);
  }

  const json = (await response.json()) as OntologyAppsApiResponse;
  return json.data?.total ?? json.data?.items?.length ?? 0;
}

/** 本体数仓模式：只保留已发布 StarRocks 映射的本体应用 */
export function filterSrMappedApps(
  apps: OntologyAppItem[],
): OntologyAppItem[] {
  return apps.filter(app => app.sr_mapping_published === true);
}

/** 本体模型模式：排除已发布数仓映射的本体应用 */
export function excludeSrMappedApps(
  apps: OntologyAppItem[],
): OntologyAppItem[] {
  return apps.filter(app => app.sr_mapping_published !== true);
}
